// What the car's effects should play, frame by frame, from what the physics did (RALLY_GOAL.md §12;
// Adam, 2026-10-12: the effects were "all just calibrated to one sound and sound tone and volume …
// not properly coordinated towards what exactly the sound is meant to come from"). Pure and
// deterministic (seeded), no Web Audio: src/audio/car-audio.ts renders it, tests/sfx.test.ts checks it.
//
//  - Tyres: each side of the car (left / right wheels) has its own mix of surface layers, from that
//    side's wheels: their surface, ground speed, load and slip. Grass, mud, water, wet and tarmac each
//    have their own layer; road roughness (how fast the suspension is working) drives a low rumble.
//  - Stones: thrown by each wheel on loose ground, at a rate set by how fast its tread turns (a locked
//    or braking wheel throws fewer, wheelspin more), the surface, and heard from that wheel's side.
//  - Braking on loose ground: the tyres plough the gravel, a darker scrub (`SideMix.scrub`) rather
//    than the bright hiss of a sideways slide.
//  - Suspension: each wheel's peak compression speed since the last frame (a pothole or bump lasts a
//    few 240 Hz steps) makes a knock (≥ 0.9 m/s) or a thump (≥ 1.6 m/s), the bump stops a clunk;
//    touching down after air a landing. Thresholds from tools/dev/sfx-probe.ts on Kelmore Forest:
//    elsewhere on the road p99 is 0.65 m/s, in the potholes the wheels cross p90 1.5 m/s.
//  - Collisions: what was hit (src/world/obstacles.ts) picks the sound, the contact force its
//    strength, where on the car it was the pan; sliding along an obstacle scrapes.
//  - Water: a tyre into a puddle splashes from its side; the car into the ford makes the big splash.
import type { SurfaceId } from '../sim/types';
import type { ObstacleMaterial } from '../sim/sim';

/** What the car hit: an obstacle's material, the terrain bank beside the road, or the ground under it. */
export type HitKind = ObstacleMaterial | 'bank' | 'ground';
export type SfxKind = 'stone' | 'knock' | 'thump' | 'bumpstop' | 'land' | 'splash' | 'bigsplash' | 'belly' | 'tap' | 'hit';

export interface SfxEvent {
  kind: SfxKind;
  /** For 'hit' and 'tap': what was hit. */
  mat: HitKind;
  /** −1 (left) … +1 (right), as heard outside the car. */
  pan: number;
  /** 0–1. */
  level: number;
  /** Playback rate (pitch). */
  rate: number;
  /** 0–1, to choose among a sound's variants. */
  pick: number;
  /** A heavy collision: crumpling metal under the material's own sound. */
  heavy: boolean;
  /** Wheel index (0–3) for wheel-borne sounds, −1 otherwise. */
  wheel: number;
}

/** One side's tyre layers (levels 0–1+) and the speed that sets their playback rate. */
export interface SideMix {
  gravelSlow: number;
  gravelFast: number;
  slide: number;
  grass: number;
  tarmac: number;
  squeal: number;
  mud: number;
  water: number;
  rumble: number;
  /** Braking scrub on loose ground (0–1): the renderer darkens this side's gravel by it. */
  scrub: number;
  /** Ground speed of this side's wheels (m/s) and their slip (0–1.5). */
  speed: number;
  slip: number;
}

/** The parts of the simulation's WheelState the sounds follow. `peakCompVel` and `landedAfter` are read and cleared. */
export interface WheelLike {
  readonly index: number;
  readonly left: boolean;
  readonly front: boolean;
  readonly susp: { readonly travel: number };
  omega: number;
  contact: boolean;
  surface: SurfaceId;
  load: number;
  slipRatio: number;
  slipAngle: number;
  compression: number;
  compressionVel: number;
  vx: number;
  peakCompVel: number;
  landedAfter: number;
}

/** A collision this frame: the largest chassis contact force (N), what it was and where on the car. */
export interface HitInput {
  force: number;
  kind: HitKind;
  /** Contact point in the car's frame (m): +left, +forward (0 when unknown). */
  left: number;
  forward: number;
}

/** Stones thrown per metre of ground speed per wheel, by surface (gravel 1). */
const LOOSENESS: Record<SurfaceId, number> = { gravel: 1, loose: 1.6, rock: 0.75, grass: 0.04, mud: 0.08, wet: 0, water: 0, tarmac: 0, concrete: 0 };
const STONES_PER_M = 0.062;
const KNOCK = 0.9;
const THUMP = 1.6;
/** A wheel back on the ground after this long in the air (s) lands (shorter: a skip, a knock). */
const LANDING_AIR = 0.18;
/** Contact force (N) below which a chassis contact is silent, and above which it is a full hit not a tap. */
const CONTACT_MIN = 4000;
const TAP_MAX = 17000;
const HEAVY = 60000;
const MAX_EVENTS = 24;

const emptySide = (): SideMix => ({ gravelSlow: 0, gravelFast: 0, slide: 0, grass: 0, tarmac: 0, squeal: 0, mud: 0, water: 0, rumble: 0, scrub: 0, speed: 0, slip: 0 });

export class SfxModel {
  readonly left = emptySide();
  readonly right = emptySide();
  /** Sustained contact with an obstacle: level 0–1, what, from where. */
  scrape = 0;
  scrapeKind: HitKind = 'concrete';
  scrapePan = 0;
  /** This frame's one-shots (`events[0 … count−1]`); the objects are reused frame to frame. */
  readonly events: SfxEvent[] = [];
  count = 0;
  private seed: number;
  private readonly stoneAcc = [0, 0, 0, 0];
  /** Each wheel's next stone, in units of the mean spacing (exponential: stones arrive at random). */
  private readonly stoneNext = [1, 1, 1, 1];
  private readonly wheelCool = [0, 0, 0, 0];
  private readonly stopCool = [0, 0, 0, 0];
  private readonly prevSurface: SurfaceId[] = ['gravel', 'gravel', 'gravel', 'gravel'];
  private readonly roughness = [0, 0, 0, 0];
  private landCool = 0;
  private hitCool = 0;
  private lastHit = 0;
  private bigSplashCool = 0;
  private contactFrames = 0;

  constructor(seed = 4711) {
    this.seed = seed;
    for (let i = 0; i < MAX_EVENTS; i++) this.events.push({ kind: 'stone', mat: 'ground', pan: 0, level: 0, rate: 1, pick: 0, heavy: false, wheel: -1 });
  }

  private rnd(): number {
    this.seed = (this.seed * 16807) % 2147483647;
    return this.seed / 2147483647;
  }

  private emit(kind: SfxKind, level: number, pan: number, rate: number, wheel = -1, mat: HitKind = 'ground', heavy = false): void {
    if (this.count >= MAX_EVENTS) return;
    const e = this.events[this.count++];
    e.kind = kind;
    e.mat = mat;
    e.pan = pan < -1 ? -1 : pan > 1 ? 1 : pan;
    e.level = level < 0 ? 0 : level > 1 ? 1 : level;
    e.rate = rate;
    e.pick = this.rnd();
    e.heavy = heavy;
    e.wheel = wheel;
  }

  /**
   * One rendered frame. `wheels`: the car's four wheels; `staticLoad`: one wheel's share of the car's
   * weight (N); `speed`: the car's speed (m/s); `hit`: the frame's largest chassis contact, or null;
   * `radius`: the tyres' rolling radius (m).
   */
  step(wheels: readonly WheelLike[], staticLoad: number, speed: number, dt: number, hit: HitInput | null, radius = 0.32): void {
    this.count = 0;
    clear(this.left);
    clear(this.right);
    this.landCool -= dt;
    this.hitCool -= dt;
    this.bigSplashCool -= dt;
    let landed = 0;
    let longestAir = 0;
    let inWater = 0;
    let entered = 0;
    for (const w of wheels) {
      const k = w.index;
      const side = w.left ? this.left : this.right;
      const pan = w.left ? -0.6 : 0.6;
      this.wheelCool[k] -= dt;
      this.stopCool[k] -= dt;
      const peak = w.peakCompVel;
      w.peakCompVel = 0;
      const air = w.landedAfter;
      w.landedAfter = 0;
      // Road roughness: how hard this wheel's suspension is working, smoothed over ~0.15 s.
      const work = w.contact ? Math.max(peak, Math.abs(w.compressionVel)) : 0;
      this.roughness[k] += (work - this.roughness[k]) * Math.min(1, dt / 0.15);
      if (air >= LANDING_AIR) {
        landed++;
        longestAir = Math.max(longestAir, air);
      } else if (air > 0.05 && this.wheelCool[k] <= 0 && speed > 3) {
        // A wheel skipping over a crest and back down: a knock from that corner.
        this.emit('knock', 0.25 + air * 2, pan, 0.95 + this.rnd() * 0.1, k);
        this.wheelCool[k] = 0.1;
      }
      if (!w.contact) {
        this.prevSurface[k] = w.surface;
        continue;
      }
      const v = Math.abs(w.vx);
      const load = Math.min(2, Math.max(0, w.load / staticLoad));
      const wgt = 0.5 * load;
      // Sliding: sideways, or the tread spinning faster than the ground (braking is the scrub below).
      const slip = Math.min(1.5, Math.max(0, w.slipRatio) + Math.abs(w.slipAngle));
      side.speed = Math.max(side.speed, v);
      side.slip = Math.max(side.slip, slip);
      const slowW = Math.min(1, v / 2.5) * (1 - Math.min(1, Math.max(0, (v - 6) / 14)));
      const fastW = Math.pow(Math.min(1.2, v / 28), 1.3);
      const slide = Math.max(0, slip - 0.08) * Math.min(1, v / 5);
      switch (w.surface) {
        case 'gravel':
        case 'loose':
        case 'rock': {
          const m = w.surface === 'loose' ? 1.25 : w.surface === 'rock' ? 0.8 : 1;
          side.gravelSlow += slowW * wgt * m * (w.surface === 'rock' ? 1.3 : 1);
          side.gravelFast += fastW * wgt * m;
          side.slide += slide * wgt * (w.surface === 'loose' ? 1.4 : 1);
          // Braking: the tread turns slower than the ground goes by (slip ratio < 0).
          side.scrub += Math.min(1, Math.max(0, -w.slipRatio) * 4) * Math.min(1, v / 4) * wgt;
          break;
        }
        case 'grass':
          side.grass += Math.min(1, v / 10) * wgt * (1 + slide);
          break;
        case 'mud':
          side.mud += Math.min(1, v / 14) * wgt * (1 + 0.6 * slide);
          break;
        case 'water':
          side.water += Math.min(1, v / 15) * wgt;
          inWater++;
          break;
        case 'wet':
          side.tarmac += 0.8 * Math.pow(Math.min(1, v / 28), 1.2) * wgt;
          side.water += 0.3 * Math.min(1, v / 20) * wgt;
          side.squeal += 0.5 * Math.max(0, slip - 0.22) * Math.min(1, v / 6) * wgt;
          break;
        default:
          side.tarmac += Math.pow(Math.min(1, v / 28), 1.2) * wgt;
          side.squeal += Math.max(0, slip - 0.22) * Math.min(1, v / 6) * wgt * 2;
      }
      side.rumble += Math.min(1, this.roughness[k] / 0.7) * Math.min(1, v / 8) * wgt;
      // Into water from dry ground: a splash from this tyre.
      if (w.surface === 'water' && this.prevSurface[k] !== 'water' && v > 3) {
        entered++;
        this.emit('splash', Math.min(1, 0.3 + v / 25), pan, 0.9 + this.rnd() * 0.2, k);
      }
      this.prevSurface[k] = w.surface;
      // Stones from this wheel.
      const loose = LOOSENESS[w.surface];
      // How fast the tread moves: stones are flung by the turning tyre, so a locked wheel throws none.
      const tread = Math.abs(w.omega * radius);
      if (loose > 0 && tread > 0.5) {
        this.stoneAcc[k] += dt * tread * loose * STONES_PER_M * (1 + 2 * Math.min(1, Math.max(0, w.slipRatio)));
        while (this.stoneAcc[k] > this.stoneNext[k]) {
          this.stoneAcc[k] -= this.stoneNext[k];
          // Random spacing, so the four wheels don't throw in step (a burst of four reads as crackle).
          this.stoneNext[k] = -Math.log(1 - this.rnd() * 0.999);
          const r = this.rnd();
          const big = this.rnd() < (w.surface === 'rock' ? 0.25 : 0.08);
          // Most stones are small and quiet; the big ones are louder and lower.
          const level = (0.2 + 0.6 * r * r) * (big ? 1.5 : 1) * (0.7 + 0.3 * Math.min(1, v / 20));
          const rate = (big ? 0.78 : 0.95) + this.rnd() * 0.3;
          // Thrown into the arch of this wheel: from its side, the rear ones a little more central.
          this.emit('stone', level, pan * (w.front ? 1 : 0.8), rate, k);
        }
      } else this.stoneAcc[k] = 0;
      // Suspension: a knock, a thump or the bump stops, from this corner.
      if (peak >= KNOCK && this.wheelCool[k] <= 0 && this.landCool <= 0.25) {
        const heavyHit = peak >= THUMP;
        const level = heavyHit ? 0.35 + (peak - THUMP) / 2.4 : 0.18 + (peak - KNOCK) / 1.4;
        // Heavier hits sound deeper; the front corners carry less of the car and ring a little higher.
        const rate = (heavyHit ? 1 - Math.min(0.12, (peak - THUMP) * 0.04) : 1) * (w.front ? 1.03 : 0.98) * (0.96 + this.rnd() * 0.08);
        this.emit(heavyHit ? 'thump' : 'knock', level, pan * 0.8, rate, k);
        this.wheelCool[k] = 0.09;
      }
      if (w.compression > w.susp.travel * 0.93 && peak > 0.8 && this.stopCool[k] <= 0) {
        this.emit('bumpstop', Math.min(1, peak / 3), pan * 0.7, 0.92 + this.rnd() * 0.16, k);
        this.stopCool[k] = 0.25;
      }
    }
    // Landing: the car comes back down after a jump (the wheels report how long they were in the air).
    if (landed && this.landCool <= 0) {
      this.emit('land', 0.4 + Math.min(0.6, longestAir * 0.8), 0, 0.9 + this.rnd() * 0.15);
      this.landCool = 0.5;
    }
    // Into the ford: the big splash once, on top of the tyres' own.
    if (entered && inWater >= 2 && speed > 8 && this.bigSplashCool <= 0) {
      this.emit('bigsplash', Math.min(1, speed / 22), 0, 0.9 + this.rnd() * 0.15);
      this.bigSplashCool = 1.5;
    }
    this.collide(hit, speed, dt, landed > 0);
    finish(this.left);
    finish(this.right);
  }

  private collide(hit: HitInput | null, speed: number, dt: number, landing: boolean): void {
    const f = hit ? hit.force : 0;
    if (!hit || f < CONTACT_MIN) {
      this.contactFrames = 0;
      this.scrape = Math.max(0, this.scrape - dt * 6);
      return;
    }
    this.contactFrames++;
    // Where on the car: the obstacle's side sets the pan (+left in the car frame is the left speaker).
    const pan = hit.kind === 'ground' ? 0 : Math.max(-1, Math.min(1, -hit.left / 1.2)) * 0.85;
    const level = Math.min(1, 0.25 + 0.3 * Math.log10(f / 8000));
    if (hit.kind === 'ground') {
      // The underbody on the ground: part of a landing, or grounding on a crest or a rock.
      if (!landing && this.hitCool <= 0) {
        this.emit('belly', 0.3 + 0.7 * Math.min(1, Math.log10(f / CONTACT_MIN) / 2), 0, 0.92 + this.rnd() * 0.16);
        this.hitCool = 0.3;
      }
    } else if (this.hitCool <= 0 || f > this.lastHit * 2) {
      const tap = f < TAP_MAX;
      this.emit(tap ? 'tap' : 'hit', level, pan, (tap ? 1 : 0.95) + this.rnd() * 0.1 - (f > HEAVY ? 0.06 : 0), -1, hit.kind, f > HEAVY);
      this.hitCool = 0.2;
      this.lastHit = f;
    }
    // Held against an obstacle while moving: a scrape.
    if (hit.kind !== 'ground' && this.contactFrames >= 3 && speed > 2) {
      const target = Math.min(1, f / 40000) * Math.min(1, speed / 8);
      this.scrape += (target - this.scrape) * Math.min(1, dt * 10);
      this.scrapeKind = hit.kind;
      this.scrapePan = pan;
    } else this.scrape = Math.max(0, this.scrape - dt * 6);
  }
}

/** Slide and squeal levels: the loops' working range. */
function finish(s: SideMix): void {
  s.slide = Math.min(1.2, 1.4 * s.slide);
  s.squeal = Math.min(1, 1.6 * s.squeal);
  s.scrub = Math.min(1, s.scrub);
}

function clear(s: SideMix): void {
  s.gravelSlow = s.gravelFast = s.slide = s.grass = s.tarmac = s.squeal = s.mud = s.water = s.rumble = s.scrub = s.speed = s.slip = 0;
}
